import {
  Input,
  Modal,
  Space,
  Typography,
  Select,
  Upload,
  DatePicker,
  Checkbox,
  notification,
  Alert,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { Form, Formik } from "formik";
import React, { useRef, useState } from "react";
import * as Yup from "yup";
import moment from "moment";

import { createNotice, updateNoticeById } from "../../store/api/notices";
import { NOTICE_CATEGORIES, NOTICE_CATEGORY_OPTIONS } from "../../constants";

const { Title, Text } = Typography;

const AddNoticePopup = ({
  closePopup,
  item,
  onCreateSuccess,
  onUpdateSuccess,
}) => {
  const formikRef = useRef(null);
  const handleCancel = () => {
    closePopup();
  };

  const [confirmLoading, setConfirmLoading] = useState(false);

  const initialValues = {
    title: item?.title ?? "",
    category: item?.category ?? null,
    file: item?.filePath ?? null,
    noticeDate: item?.noticeDate ? moment(item?.noticeDate) : null,
    isPublished: item?.isPublished || false,
    description: item?.description ?? "",
    showOnPopup: item?.showOnPopup || false,
    showOnPopupUntil: item?.showOnPopupUntil
      ? moment(item?.showOnPopupUntil)
      : null,
  };

  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    category: Yup.string().required("Category is required"),
    file: Yup.mixed().required("File is required"),
    noticeDate: Yup.string().required("Notice date is required"),
    description: Yup.string().nullable(),
    isPublished: Yup.boolean(),
    showOnPopup: Yup.boolean(),
    showOnPopupUntil: Yup.string().nullable(),
  });

  const addNotice = (values) => {
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("category", values.category);
    formData.append("file", values.file);
    formData.append("noticeDate", values.noticeDate);
    formData.append(
      "description",
      values.category === NOTICE_CATEGORIES.HR ? values.description : ""
    );
    formData.append("isPublished", values.isPublished);
    formData.append("showOnPopup", values.showOnPopup);
    if (values.showOnPopup && values.showOnPopupUntil) {
      formData.append("showOnPopupUntil", values.showOnPopupUntil);
    }

    createNotice(formData)
      .then(({ data }) => {
        setConfirmLoading(false);
        closePopup();
        notification.success({ message: data.message });
        !!onCreateSuccess && onCreateSuccess();
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
      });
  };

  const updateNotice = (values) => {
    const data = {
      title: values.title,
      category: values.category,
      id: item.id,
      noticeDate: values.noticeDate,
      description:
        values.category === NOTICE_CATEGORIES.HR ? values.description : "",
      isPublished: values.isPublished,
      showOnPopup: values.showOnPopup,
      showOnPopupUntil:
        values.showOnPopup && values.showOnPopupUntil
          ? values.showOnPopupUntil
          : null,
    };

    updateNoticeById(item.id, data)
      .then(({ data }) => {
        setConfirmLoading(false);
        closePopup();
        notification.success({ message: data.message });
        !!onUpdateSuccess && onUpdateSuccess();
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
      });
  };

  const onSubmit = (values) => {
    setConfirmLoading(true);

    if (item) {
      updateNotice(values);
    } else {
      addNotice(values);
    }
  };

  return (
    <>
      <Modal
        title={item ? "Edit Notice" : "Add Notice"}
        open={true}
        confirmLoading={confirmLoading}
        onOk={() => {
          formikRef.current.submitForm();
        }}
        width={768}
        onCancel={handleCancel}
      >
        <>
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ errors, touched, handleChange, values, setFieldValue }) => {
              return (
                <Form>
                  <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size={20}
                  >
                    <div>
                      <Title level={5}>Category *</Title>
                      <Select
                        style={{ width: "100%" }}
                        placeholder="Select category"
                        name="category"
                        value={values.category}
                        onChange={(value) => setFieldValue("category", value)}
                      >
                        {NOTICE_CATEGORY_OPTIONS.map(({ label, value }) => (
                          <Select.Option value={value} key={value}>
                            {label}
                          </Select.Option>
                        ))}
                      </Select>
                      {errors.category && touched.category ? (
                        <Text type="danger">{errors.category}</Text>
                      ) : null}
                    </div>
                    <div>
                      <Title level={5}>Title *</Title>
                      <Input
                        placeholder="Enter notice title"
                        name="title"
                        value={values.title}
                        onChange={handleChange}
                      />
                      {errors.title && touched.title ? (
                        <Text type="danger">{errors.title}</Text>
                      ) : null}
                    </div>

                    <div>
                      <Title level={5}>Notice Date *</Title>
                      <DatePicker
                        style={{ width: "100%" }}
                        placeholder="Enter notice date"
                        name="noticeDate"
                        value={values.noticeDate}
                        onChange={(date) => setFieldValue("noticeDate", date)}
                        format="MM/DD/YYYY"
                        disabledDate={(current) => {
                          return moment().add(0, "days") < current;
                        }}
                      />
                      {errors.noticeDate && touched.noticeDate ? (
                        <Text type="danger">{errors.noticeDate}</Text>
                      ) : null}
                    </div>
                    {!item && (
                      <div>
                        <Title level={5}>Upload File *</Title>
                        <Alert
                          message="Recommended file size: max 1MB."
                          type="warning"
                          style={{ marginTop: "4px", marginBottom: "16px" }}
                        ></Alert>
                        <Upload.Dragger
                          name="file"
                          action={"/"}
                          multiple={false}
                          fileList={values.file ? [values.file] : []}
                          onChange={(value) =>
                            setFieldValue("file", value.file.originFileObj)
                          }
                          accept=".pdf,.jpeg,.jpg,.png"
                        >
                          <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                          </p>
                          <p className="ant-upload-text">
                            Click or drag file to this area to upload
                          </p>
                          <p className="ant-upload-hint">
                            Support for a .pdf, .jpeg, .jpg, .png
                          </p>
                        </Upload.Dragger>

                        {errors.file && touched.file ? (
                          <Text type="danger">{errors.file}</Text>
                        ) : null}
                      </div>
                    )}

                    {values.category === NOTICE_CATEGORIES.HR && (
                      <div>
                        <Title level={5}>Application Url</Title>
                        <Input
                          placeholder="e.g. https://forms.gle/xxxxxxxx"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                        />
                        {errors.description && touched.description ? (
                          <Text type="danger">{errors.description}</Text>
                        ) : null}
                      </div>
                    )}

                    <Space>
                      <Checkbox
                        name="isPublished"
                        checked={values.isPublished}
                        onChange={(e) =>
                          setFieldValue("isPublished", e.target.checked)
                        }
                      >
                        Publish on website
                      </Checkbox>
                      <Checkbox
                        name="showOnPopup"
                        checked={values.showOnPopup}
                        onChange={(e) =>
                          setFieldValue("showOnPopup", e.target.checked)
                        }
                      >
                        Show on popup
                      </Checkbox>
                      {values.showOnPopup && (
                        <Space>
                          <span>Show on popup until</span>
                          <DatePicker
                            style={{ width: "100%" }}
                            placeholder="Select date"
                            name="showOnPopupUntil"
                            value={values.showOnPopupUntil}
                            onChange={(date) =>
                              setFieldValue("showOnPopupUntil", date)
                            }
                            format="MM/DD/YYYY"
                            disabledDate={(current) => {
                              return moment().add(0, "days") > current;
                            }}
                          />
                        </Space>
                      )}
                    </Space>
                    {values.showOnPopup && !values.isPublished && (
                      <Space>
                        <p style={{ color: "#999" }}>
                          Note: Notice will show on popup only after it is
                          published.
                        </p>
                      </Space>
                    )}
                  </Space>
                </Form>
              );
            }}
          </Formik>
        </>
      </Modal>
    </>
  );
};

export default AddNoticePopup;
